import { LearningCard } from '@/components/features/learning/content/LearningCard';
import { FolderCard } from './FolderCard';
import type {
  FolderItem,
  MyLearningItem,
  UserLessonItem,
} from '@/lib/api-client';

export interface MyLearningGridProps {
  /** The unified folder + lesson stream, already sorted and paginated. */
  items: MyLearningItem[];
  onOpenFolder: (folder: FolderItem) => void;
  onRenameFolder: (folder: FolderItem) => void;
  onDeleteFolder: (folder: FolderItem) => void;
  onContinueLesson: (lesson: UserLessonItem) => void;
  onRetryLesson: (lesson: UserLessonItem) => void;
  onDeleteLesson: (lesson: UserLessonItem) => void;
  onMoveLesson: (lesson: UserLessonItem) => void;
  onEditLessonDetails: (lesson: UserLessonItem) => void;
}

/**
 * The card grid: folders and lessons in ONE recency-sorted stream, not two
 * sections.
 *
 * Both orderings and the page slice are the server's — `entry.type` is the only
 * decision made here. That is why pagination can span both types and deleting a
 * folder shifts lessons up a page.
 *
 * Whole rows of props rather than one handler taking a discriminated union: the
 * two card components have genuinely different action sets, and collapsing them
 * would mean every caller re-narrowing the type it already knew.
 */
export function MyLearningGrid({
  items,
  onOpenFolder,
  onRenameFolder,
  onDeleteFolder,
  onContinueLesson,
  onRetryLesson,
  onDeleteLesson,
  onMoveLesson,
  onEditLessonDetails,
}: MyLearningGridProps) {
  return (
    // Below `sm` the cards draw as compact rows (see `LearningCard`,
    // `FolderCard`), so the 250px row floor would leave each one mostly
    // empty: rows size to content and sit closer together there.
    <div className='mb-8 grid grid-cols-1 gap-3 sm:auto-rows-[minmax(250px,_auto)] sm:gap-6 md:grid-cols-2 lg:grid-cols-3'>
      {items.map(entry =>
        entry.type === 'folder' ? (
          <FolderCard
            key={`folder-${entry.folder.folder_id}`}
            folder={entry.folder}
            onOpen={onOpenFolder}
            onRename={onRenameFolder}
            onDelete={onDeleteFolder}
          />
        ) : (
          <LearningCard
            key={`lesson-${entry.lesson.lesson_id}`}
            currentVersion={entry.lesson.current_version}
            item={{
              id: entry.lesson.lesson_id,
              title: entry.lesson.title,
              description: entry.lesson.description,
              tags: entry.lesson.tags,
              status: entry.lesson.status,
              error: entry.lesson.error,
            }}
            onContinue={() => onContinueLesson(entry.lesson)}
            onRetry={() => onRetryLesson(entry.lesson)}
            onDelete={() => onDeleteLesson(entry.lesson)}
            onMove={() => onMoveLesson(entry.lesson)}
            onEditTags={() => onEditLessonDetails(entry.lesson)}
          />
        )
      )}
    </div>
  );
}
